import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

type Props = {
  expiresAt: Date | string | number
}

function getTimeLeft(target: Date | string | number) {
  const safeTarget = toDate(target)
  const total = Math.max(0, safeTarget.getTime() - Date.now())
  const secondsTotal = Math.floor(total / 1_000)
  const days = Math.floor(secondsTotal / 86_400)
  const hours = Math.floor((secondsTotal % 86_400) / 3_600)
  const mins = Math.floor((secondsTotal % 3_600) / 60)
  const secs = secondsTotal % 60

  return {
    total,
    days: String(days).padStart(2, '0'),
    hours: String(hours).padStart(2, '0'),
    minutes: String(mins).padStart(2, '0'),
    seconds: String(secs).padStart(2, '0'),
  }
}

function toDate(value: Date | string | number): Date {
  return value instanceof Date ? value : new Date(value)
}

const TimeSegment = ({
  value,
  label,
}: {
  value: string
  label: string
}) => (
  <div className="flex flex-col items-center">
    <div className="flex items-center justify-center rounded-md bg-white px-3 py-2 min-w-[54px]">
      <span className="text-2xl font-extrabold text-black leading-none">
        {value}
      </span>
    </div>
    <span className="mt-1 text-[10px] font-semibold tracking-wide text-white">
      {label}
    </span>
  </div>
)

const FlipDigit = ({ digit }: { digit: string }) => {
  const variants = {
    initial: { rotateX: 90, opacity: 0 },
    animate: { rotateX: 0, opacity: 1, transition: { duration: 0.3 } },
    exit: { rotateX: -90, opacity: 0, transition: { duration: 0.3 } },
  }

  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={digit}
        className="inline-block w-4"
        variants={variants}
        initial="initial"
        animate="animate"
        exit="exit"
        style={{ perspective: 200 }}
      >
        {digit}
      </motion.span>
    </AnimatePresence>
  )
}

/* ----------- Static zero state shown during SSR ------------ */
const ZERO_TIME_LEFT = {
  total: 0,
  days: '00',
  hours: '00',
  minutes: '00',
  seconds: '00',
}

export default function LightningDealFooter({ expiresAt }: Props) {
  const [timeLeft, setTimeLeft] = useState(ZERO_TIME_LEFT)

  useEffect(() => {
    /* Kick-off first calculation immediately, then every second. */
    const update = () => setTimeLeft(getTimeLeft(expiresAt))
    update()
    const id = setInterval(update, 1_000)
    return () => clearInterval(id)
  }, [expiresAt])

  const { total, days, hours, minutes, seconds } = timeLeft

  return (
    <div className="mt-4 flex flex-col items-center gap-2 text-white">
      <span className="text-xs font-bold tracking-widest">ENDS IN:</span>
      
      {total > 0 ? (
        <div className="flex items-end gap-3">
          {Number(days) > 0 && <TimeSegment value={days} label="DAYS" />}
          {<TimeSegment value={hours} label="HOURS" />}
          {<TimeSegment value={minutes} label="MINUTES" />}
          {<TimeSegment value={seconds} label="SECONDS" />}
        </div>
      ) : (
        <span className="text-lg font-semibold">00:00:00</span>
      )}
    </div>
  )
}
